class ImageWithTextCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.slides = [];
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleSlideChange = this.handleSlideChange.bind(this);
    this.handlePaginationClick = this.handlePaginationClick.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handleKeyboard = this.handleKeyboard.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-image-with-text-carousel'});
    this.init();

    if (window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
    }
  }

  init() {
    const sliderWrapper = this.querySelector('[data-carousel-slider-wrapper]');
    if (!sliderWrapper) return;

    const swiperEl = this.querySelector('.swiper');
    if (!swiperEl) return;

    this.slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
    if (this.slides.length === 0) return;

    // Set initial state for all slides
    this.slides.forEach((slide, i) => {
      if (i === 0) {
        slide.classList.add('active');
        slide.style.opacity = '1';
        // First slide stays relative to maintain wrapper height
        if (i === 0) {
          slide.style.position = 'relative';
        }
        slide.style.zIndex = '2';
      } else {
        slide.style.opacity = '0';
        slide.style.position = 'absolute';
        slide.style.zIndex = '1';
      }
    });

    const prevPaginationBtn = sliderWrapper.querySelector('.image-with-text-carousel__pagination-arrow--prev');
    const nextPaginationBtn = sliderWrapper.querySelector('.image-with-text-carousel__pagination-arrow--next');
    const paginationNumbers = sliderWrapper.querySelectorAll('.image-with-text-carousel__pagination-number');

    // Store references
    this.prevPaginationBtn = prevPaginationBtn;
    this.nextPaginationBtn = nextPaginationBtn;
    this.paginationNumbers = paginationNumbers;
    this.swiperEl = swiperEl;
    this.sliderWrapper = sliderWrapper;

    // Add event listeners
    if (prevPaginationBtn) {
      prevPaginationBtn.addEventListener('click', this.handlePrevClick);
    }
    if (nextPaginationBtn) {
      nextPaginationBtn.addEventListener('click', this.handleNextClick);
    }
    paginationNumbers.forEach((btn, index) => {
      btn.addEventListener('click', () => this.handlePaginationClick(index));
    });

    // Keyboard navigation
    this.addEventListener('keydown', this.handleKeyboard);

    // Touch/swipe support
    this.swiperEl.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.swiperEl.addEventListener('touchend', this.handleTouchEnd, { passive: true });

    // Handle window resize for pagination
    this.handleResize = () => {
      this.updatePagination();
    };
    window.addEventListener('resize', this.handleResize);

    // Initialize first slide
    this.goToSlide(0, false);
  }

  handleKeyboard(e) {
    if (e.key === 'ArrowLeft') {
      this.handlePrevClick();
    } else if (e.key === 'ArrowRight') {
      this.handleNextClick();
    }
  }

  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
  }

  handleTouchEnd(e) {
    if (!this.touchStartX) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = this.touchStartX - touchEndX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.handleNextClick();
      } else {
        this.handlePrevClick();
      }
    }
    this.touchStartX = null;
  }

  goToSlide(index, animate = true) {
    if (index < 0 || index >= this.slides.length) return;

    const previousIndex = this.currentIndex;
    const previousSlide = previousIndex >= 0 ? this.slides[previousIndex] : null;
    this.currentIndex = index;

    // First, make previous slide absolute (if it exists and was relative)
    if (previousSlide && previousSlide.classList.contains('active')) {
      previousSlide.style.position = 'absolute';
      previousSlide.style.opacity = '0';
      previousSlide.classList.remove('active');
      previousSlide.style.zIndex = '1';
    }

    // Then, make the new slide relative and visible
    const nextSlide = this.slides[index];
    nextSlide.style.position = 'relative';
    nextSlide.style.zIndex = '2';
    nextSlide.classList.add('active');
    
    // Fade in with smooth transition
    if (animate && previousIndex !== index) {
      // Small delay to ensure position change is applied
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          nextSlide.style.opacity = '1';
        });
      });
    } else {
      nextSlide.style.opacity = '1';
    }

    // Make all other slides absolute
    this.slides.forEach((slide, i) => {
      if (i !== index) {
        slide.style.position = 'absolute';
        slide.style.opacity = '0';
        slide.classList.remove('active');
        slide.style.zIndex = '1';
      }
    });

    // Update pagination
    this.updatePagination();
    this.updateNavigationButtons();
    this.handleSlideChange();
  }

  handleSlideChange() {
    // Dispatch custom event for any listeners
    this.dispatchEvent(new CustomEvent('slide:change', {
      detail: { index: this.currentIndex }
    }));
  }

  updatePagination() {
    if (!this.paginationNumbers) return;
    
    const isMobile = window.innerWidth <= 767;
    const totalSlides = this.slides.length;
    
    if (isMobile && totalSlides > 4) {
      // Mobile: show sliding window of 4 numbers
      // Try to center current slide, but adjust for boundaries
      const windowSize = 4;
      let startIndex = Math.max(0, this.currentIndex - 1);
      let endIndex = Math.min(totalSlides - 1, startIndex + windowSize - 1);
      
      // If we're at the end, adjust start to show windowSize numbers
      if (endIndex === totalSlides - 1 && endIndex - startIndex < windowSize - 1) {
        startIndex = Math.max(0, endIndex - windowSize + 1);
      }
      
      // If we're at the beginning, ensure we show windowSize numbers
      if (startIndex === 0 && endIndex - startIndex < windowSize - 1) {
        endIndex = Math.min(totalSlides - 1, startIndex + windowSize - 1);
      }
      
      // Show/hide pagination numbers
      this.paginationNumbers.forEach((btn, index) => {
        if (index >= startIndex && index <= endIndex) {
          btn.style.display = 'flex';
          if (index === this.currentIndex) {
            btn.classList.add('active');
          } else {
            btn.classList.remove('active');
          }
        } else {
          btn.style.display = 'none';
          btn.classList.remove('active');
        }
      });
    } else {
      // Desktop or <= 4 slides: show all numbers
      this.paginationNumbers.forEach((btn, index) => {
        btn.style.display = 'flex';
        if (index === this.currentIndex) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
    }
  }

  updateNavigationButtons() {
    const isBeginning = this.currentIndex === 0;
    const isEnd = this.currentIndex === this.slides.length - 1;

    // Update pagination arrows
    if (this.prevPaginationBtn) {
      this.prevPaginationBtn.disabled = isBeginning;
      this.prevPaginationBtn.style.opacity = isBeginning ? '0.3' : '1';
    }
    if (this.nextPaginationBtn) {
      this.nextPaginationBtn.disabled = isEnd;
      this.nextPaginationBtn.style.opacity = isEnd ? '0.3' : '1';
    }
  }

  handlePaginationClick(index) {
    this.goToSlide(index);
  }

  handlePrevClick() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  handleNextClick() {
    if (this.currentIndex < this.slides.length - 1) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  handleBlockSelect(e) {
    const sectionId = e.detail.sectionId;
    if (this.sectionId != sectionId) return;

    const slideEl = e.target.closest('.swiper-slide');
    if (!slideEl) return;

    const slideIndex = parseInt(slideEl.dataset.swiperSlideIndex, 10);
    if (!isNaN(slideIndex)) {
      this.goToSlide(slideIndex, false);
    }
  }

  disconnectedCallback() {
    if (this.prevPaginationBtn) {
      this.prevPaginationBtn.removeEventListener('click', this.handlePrevClick);
    }
    if (this.nextPaginationBtn) {
      this.nextPaginationBtn.removeEventListener('click', this.handleNextClick);
    }
    if (this.paginationNumbers) {
      this.paginationNumbers.forEach((btn, index) => {
        btn.removeEventListener('click', () => this.handlePaginationClick(index));
      });
    }
    if (this.swiperEl) {
      this.swiperEl.removeEventListener('touchstart', this.handleTouchStart);
      this.swiperEl.removeEventListener('touchend', this.handleTouchEnd);
    }
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    this.removeEventListener('keydown', this.handleKeyboard);

    if (window.Shopify.designMode) {
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }
}

if (!customElements.get('image-with-text-carousel')) {
  customElements.define('image-with-text-carousel', ImageWithTextCarousel);
}

